/**
 * Pure data-transformation helpers for VisualFraction.
 */

/** Sum all group counts. */
export function sumCounts(counts: readonly number[]): number {
  return counts.reduce((sum, count) => sum + count, 0);
}

/**
 * Expand group counts into a flat array of group indices.
 * e.g. [2, 1] → [0, 0, 1]
 */
export function countsToPartGroups(counts: readonly number[]): number[] {
  return counts.flatMap((count, groupIndex) =>
    Array.from({ length: count }, () => groupIndex),
  );
}

/**
 * Clamp each element of `value` into the range [0, expected[i]].
 * Returns zeros when `value` is absent.
 */
export function normalizeCounts(
  value: number[] | undefined,
  expected: readonly number[],
): number[] {
  if (!Array.isArray(value)) return expected.map(() => 0);
  return expected.map((max, i) => Math.max(0, Math.min(max, value[i] ?? 0)));
}

/** Stable string key for a count array — used to skip redundant syncs. */
export function serializeCounts(value: readonly number[] | undefined): string | null {
  return Array.isArray(value) ? value.join('|') : null;
}

/**
 * Build a per-slot assignment array from group counts.
 * Filled slots (indices < filledCount) are always null in the output —
 * they are handled separately via `countsToPartGroups`.
 */
export function countsToAssignments(
  counts: readonly number[],
  divisions: number,
  filledCount: number,
): Array<number | null> {
  const assignments: Array<number | null> = Array.from({ length: divisions }, () => null);
  let index = filledCount;

  counts.forEach((count, groupIndex) => {
    for (let i = 0; i < count && index < divisions; i++) {
      assignments[index] = groupIndex;
      index++;
    }
  });

  return assignments;
}

/**
 * Cycle the assignment of `partIndex` through:
 *   null → 0 → 1 → … → (groupCount - 1) → null
 */
export function getNextAssignments(
  assignments: readonly (number | null)[],
  partIndex: number,
  groupCount: number,
): Array<number | null> {
  if (groupCount === 0) return [...assignments];

  const current = assignments[partIndex] ?? null;
  const next =
    current === null ? 0
    : current + 1 >= groupCount ? null
    : current + 1;

  const updated = [...assignments];
  updated[partIndex] = next;
  return updated;
}

/**
 * Reduce a slot-assignment array back to per-group counts.
 */
export function assignmentsToCounts(
  assignments: readonly (number | null)[],
  groupCount: number,
): number[] {
  const counts = Array.from({ length: groupCount }, () => 0);
  for (const groupIndex of assignments) {
    if (groupIndex !== null && groupIndex >= 0 && groupIndex < groupCount) {
      counts[groupIndex] = (counts[groupIndex] ?? 0) + 1;
    }
  }
  return counts;
}
